/**
 * Business Partners (CRM Lite)
 * Basic client management for Finance module users
 * - Add/Edit/View clients
 * - No deals, opportunities, or advanced CRM features
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Plus, 
  Search,
  Pencil,
  Trash2,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BusinessPartners = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: 'UAE',
    vat_number: '',
    type: 'customer',
    notes: ''
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/clients`);
      setPartners(res.data);
    } catch (err) {
      console.error('Error fetching partners:', err);
      toast.error(isRTL ? 'فشل في تحميل العملاء' : 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error(isRTL ? 'الاسم مطلوب' : 'Name is required');
      return;
    }

    try {
      if (editingPartner) {
        await axios.put(`${API_URL}/api/crm/clients/${editingPartner.id}`, formData);
        toast.success(isRTL ? 'تم تحديث العميل' : 'Partner updated');
      } else {
        await axios.post(`${API_URL}/api/crm/clients`, {
          ...formData,
          tags: [formData.type === 'supplier' ? 'Supplier' : 'Customer']
        });
        toast.success(isRTL ? 'تم إضافة العميل' : 'Partner added');
      }
      setShowDialog(false);
      resetForm();
      fetchPartners();
    } catch (err) {
      console.error('Error saving partner:', err);
      toast.error(isRTL ? 'فشل في الحفظ' : 'Failed to save');
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name || '',
      company: partner.company || '',
      email: partner.email || '',
      phone: partner.phone || '',
      address: partner.address || '',
      city: partner.city || '',
      country: partner.country || 'UAE',
      vat_number: partner.vat_number || '',
      type: partner.tags?.includes('Supplier') ? 'supplier' : 'customer',
      notes: partner.notes || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isRTL ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/crm/clients/${id}`);
      toast.success(isRTL ? 'تم الحذف' : 'Deleted');
      fetchPartners();
    } catch (err) {
      toast.error(isRTL ? 'فشل في الحذف' : 'Failed to delete');
    }
  };

  const resetForm = () => {
    setEditingPartner(null);
    setFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: 'UAE',
      vat_number: '',
      type: 'customer',
      notes: ''
    });
  };

  const openAddDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const filteredPartners = partners.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: partners.length,
    customers: partners.filter(p => !p.tags?.includes('Supplier')).length,
    suppliers: partners.filter(p => p.tags?.includes('Supplier')).length
  };

  return (
    <div className="space-y-6" data-testid="business-partners-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            {isRTL ? 'شركاء الأعمال' : 'Business Partners'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isRTL ? 'إدارة العملاء والموردين للفواتير' : 'Manage customers & suppliers for invoicing'}
          </p>
        </div>
        <Button onClick={openAddDialog} className="gap-2" data-testid="add-partner-btn">
          <Plus className="h-4 w-4" />
          {isRTL ? 'إضافة شريك' : 'Add Partner'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{isRTL ? 'إجمالي الشركاء' : 'Total Partners'}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Building2 className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{isRTL ? 'العملاء' : 'Customers'}</p>
                <p className="text-2xl font-bold">{stats.customers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{isRTL ? 'الموردين' : 'Suppliers'}</p>
                <p className="text-2xl font-bold">{stats.suppliers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
            <Input
              placeholder={isRTL ? 'بحث عن شريك...' : 'Search partners...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={isRTL ? 'pr-10' : 'pl-10'}
              data-testid="search-partners"
            />
          </div>
        </CardContent>
      </Card>

      {/* Partners Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredPartners.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{isRTL ? 'لا يوجد شركاء' : 'No partners found'}</p>
              <Button variant="link" onClick={openAddDialog}>
                {isRTL ? 'إضافة أول شريك' : 'Add your first partner'}
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'الاسم' : 'Name'}</TableHead>
                  <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'الشركة' : 'Company'}</TableHead>
                  <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'البريد الإلكتروني' : 'Email'}</TableHead>
                  <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'الهاتف' : 'Phone'}</TableHead>
                  <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'النوع' : 'Type'}</TableHead>
                  <TableHead className={isRTL ? 'text-right' : ''}>{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPartners.map((partner) => (
                  <TableRow key={partner.id} data-testid={`partner-row-${partner.id}`}>
                    <TableCell className={`font-medium ${isRTL ? 'text-right' : ''}`}>
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {partner.name?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                        {partner.name}
                      </div>
                    </TableCell>
                    <TableCell className={isRTL ? 'text-right' : ''}>{partner.company || '-'}</TableCell>
                    <TableCell className={isRTL ? 'text-right' : ''}>
                      {partner.email ? (
                        <a href={`mailto:${partner.email}`} className="text-primary hover:underline">
                          {partner.email}
                        </a>
                      ) : '-'}
                    </TableCell>
                    <TableCell className={isRTL ? 'text-right' : ''}>
                      {partner.phone ? (
                        <a href={`tel:${partner.phone}`} className="text-primary hover:underline">
                          {partner.phone}
                        </a>
                      ) : '-'}
                    </TableCell>
                    <TableCell className={isRTL ? 'text-right' : ''}>
                      <Badge variant={partner.tags?.includes('Supplier') ? 'secondary' : 'default'}>
                        {partner.tags?.includes('Supplier') 
                          ? (isRTL ? 'مورد' : 'Supplier') 
                          : (isRTL ? 'عميل' : 'Customer')}
                      </Badge>
                    </TableCell>
                    <TableCell className={isRTL ? 'text-right' : ''}>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleEdit(partner)}
                          data-testid={`edit-partner-${partner.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(partner.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-partner-${partner.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPartner 
                ? (isRTL ? 'تعديل شريك' : 'Edit Partner')
                : (isRTL ? 'إضافة شريك جديد' : 'Add New Partner')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isRTL ? 'الاسم' : 'Name'} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={isRTL ? 'اسم الشخص أو جهة الاتصال' : 'Contact name'}
                  data-testid="partner-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'الشركة' : 'Company'}</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder={isRTL ? 'اسم الشركة' : 'Company name'}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isRTL ? 'البريد الإلكتروني' : 'Email'}</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'الهاتف' : 'Phone'}</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+971 50 123 4567"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isRTL ? 'النوع' : 'Type'}</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData({...formData, type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">{isRTL ? 'عميل' : 'Customer'}</SelectItem>
                    <SelectItem value="supplier">{isRTL ? 'مورد' : 'Supplier'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'الرقم الضريبي' : 'VAT Number'}</Label>
                <Input
                  value={formData.vat_number}
                  onChange={(e) => setFormData({...formData, vat_number: e.target.value})}
                  placeholder="123456789012345"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'العنوان' : 'Address'}</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder={isRTL ? 'عنوان الشارع' : 'Street address'}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isRTL ? 'المدينة' : 'City'}</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder={isRTL ? 'دبي' : 'Dubai'}
                />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'الدولة' : 'Country'}</Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(v) => setFormData({...formData, country: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UAE">{isRTL ? 'الإمارات' : 'UAE'}</SelectItem>
                    <SelectItem value="Saudi Arabia">{isRTL ? 'السعودية' : 'Saudi Arabia'}</SelectItem>
                    <SelectItem value="Qatar">{isRTL ? 'قطر' : 'Qatar'}</SelectItem>
                    <SelectItem value="Kuwait">{isRTL ? 'الكويت' : 'Kuwait'}</SelectItem>
                    <SelectItem value="Bahrain">{isRTL ? 'البحرين' : 'Bahrain'}</SelectItem>
                    <SelectItem value="Oman">{isRTL ? 'عمان' : 'Oman'}</SelectItem>
                    <SelectItem value="Other">{isRTL ? 'أخرى' : 'Other'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder={isRTL ? 'ملاحظات إضافية...' : 'Additional notes...'}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleSubmit} data-testid="save-partner-btn">
              {editingPartner 
                ? (isRTL ? 'تحديث' : 'Update')
                : (isRTL ? 'إضافة' : 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessPartners;
